export const RIDE_REPORT_TYPES = {
  RIDES_REPORT: 'ride-status-overview',
  INCOMPLETE_REPORT: 'incomplete-ride-breakdown',
  LS_INCOMPLETE_REPORT: 'life-sustaining-incomplete-ride-breakdown',
  COST_BY_COUNTY: 'cost-by-county',
  RIDE_OTP_PERFORMANCE: 'rides-on-time-performance',
  RENDERING_SERVICE: 'top-5-rendering-services',
  RIDE_LS_INTERVAL: 'life-sustaining-rides-&-on-time-performance',
  RIDE_MEMBER_DATA: 'rides-and-members',
  ON_TIME_PERFORMANCE: 'on-time-performance',
  RIDE_LS_ROUTINE: 'life-sustaining-vs-routine',
  RIDE_STATUS_OVERVIEW: 'rides-by-status',
  RIDE_COUNTY: 'rides-by-county',
  RIDE_AVERAGE_MILES: 'average-miles-traveled-per-ride',
  RIDE_HEAT_MAP: 'rides-by-pickup-time-heat-map',
  CONTRACTOR_TOP_COST_MEMBERS: 'contractor-top-cost-members',
} as const;

export type ReportType =
  (typeof RIDE_REPORT_TYPES)[keyof typeof RIDE_REPORT_TYPES];

export const rideHideDashboardRoutes = [
  RIDE_REPORT_TYPES.RIDES_REPORT,
  RIDE_REPORT_TYPES.INCOMPLETE_REPORT,
  RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT,
  RIDE_REPORT_TYPES.COST_BY_COUNTY,
  RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE,
  RIDE_REPORT_TYPES.RENDERING_SERVICE,
  RIDE_REPORT_TYPES.RIDE_LS_INTERVAL,
  RIDE_REPORT_TYPES.RIDE_MEMBER_DATA,
  RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE,
  RIDE_REPORT_TYPES.RIDE_LS_ROUTINE,
  RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW,
  RIDE_REPORT_TYPES.RIDE_COUNTY,
  RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES,
  RIDE_REPORT_TYPES.RIDE_HEAT_MAP,
];

export const excelExportKeys: Record<string, string> = {
  [RIDE_REPORT_TYPES.RIDES_REPORT]: 'report-data',
  [RIDE_REPORT_TYPES.INCOMPLETE_REPORT]: 'incomplete-ride',
  [RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT]: 'ls-incomplete-ride',
  [RIDE_REPORT_TYPES.COST_BY_COUNTY]: 'cost-by-county',
  [RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE]: 'otp-interval',
  [RIDE_REPORT_TYPES.RENDERING_SERVICE]: 'rendering-service',
  [RIDE_REPORT_TYPES.RIDE_LS_INTERVAL]: 'ls-otp-interval',
  [RIDE_REPORT_TYPES.RIDE_MEMBER_DATA]: 'member',
  [RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE]: 'on-time-performance',
  [RIDE_REPORT_TYPES.RIDE_LS_ROUTINE]: 'ls-routine',
  [RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW]: 'by-status',
  [RIDE_REPORT_TYPES.RIDE_COUNTY]: 'by-county',
  [RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES]: 'average-miles',
  [RIDE_REPORT_TYPES.RIDE_HEAT_MAP]: 'heatmap',
};

export const tableType = {
  custom: [
    RIDE_REPORT_TYPES.RIDES_REPORT,
    RIDE_REPORT_TYPES.INCOMPLETE_REPORT,
    RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT,
    RIDE_REPORT_TYPES.COST_BY_COUNTY,
    RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE,
    RIDE_REPORT_TYPES.RENDERING_SERVICE,
    RIDE_REPORT_TYPES.RIDE_LS_INTERVAL,
  ],
  monthly: [
    RIDE_REPORT_TYPES.RIDE_MEMBER_DATA,
    RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE,
    RIDE_REPORT_TYPES.RIDE_LS_ROUTINE,
    RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW,
    RIDE_REPORT_TYPES.RIDE_COUNTY,
    RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES,
    RIDE_REPORT_TYPES.RIDE_HEAT_MAP,
  ],
};

export const aggregatedRideReportTypes: ReportType[] = [
  RIDE_REPORT_TYPES.COST_BY_COUNTY,
  RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE,
  RIDE_REPORT_TYPES.RENDERING_SERVICE,
  RIDE_REPORT_TYPES.RIDE_LS_INTERVAL,
];

export const RIDE_REPORT_TABLE_TITLES = {
  RIDE_STATUS_OVERVIEW: 'Ride Status Overview',
  INCOMPLETE_RIDE_BREAKDOWN: 'Incomplete Ride Breakdown',
  LIFE_SUSTAINING_INCOMPLETE_RIDE_BREAKDOWN:
    'Life Sustaining Incomplete Ride Breakdown',
  COST_BY_COUNTY: 'Cost by County',
  RIDES_ON_TIME_PERFORMANCE: 'Rides & On Time Performance',
  TOP_5_RENDERING_SERVICES: 'Top 5 Rendering Services',
  LIFE_SUSTAINING_RIDES_ON_TIME_PERFORMANCE:
    'Life Sustaining Rides & On Time Performance',
  RIDES_AND_MEMBERS: 'Rides and Members',
  ON_TIME_PERFORMANCE: 'On Time Performance',
  LIFE_SUSTAINING_VS_ROUTINE: 'Life Sustaining vs Routine',
  RIDES_BY_STATUS: 'Rides by Status',
  RIDES_BY_COUNTY: 'Rides by County',
  AVERAGE_MILES_TRAVELED_PER_RIDE: 'Average Miles Traveled Per Ride',
  RIDES_BY_PICKUP_TIME_HEAT_MAP: 'Rides by Pickup Time Heat Map',
};

export const RIDE_REPORT_TABLE_DESCRIPTIONS = {
  RIDE_STATUS_OVERVIEW:
    'Shows the total number of rides grouped by status (e.g., completed, cancelled, incomplete) for the selected date range, helping you quickly understand overall ride outcomes.',
  INCOMPLETE_RIDE_BREAKDOWN:
    'Displays all rides marked as incomplete during the selected date range, allowing you to identify gaps or failed ride fulfillments that may require follow-up.',
  LIFE_SUSTAINING_INCOMPLETE_RIDE_BREAKDOWN:
    'Highlights incomplete rides for life-sustaining services within the selected date range, helping prioritize urgent issues that may impact member care.',
  COST_BY_COUNTY:
    'Shows total ride costs, for rides that incurred a cost, by county for the selected date range, giving visibility into where transportation spending is highest.',
  RIDES_ON_TIME_PERFORMANCE:
    'Displays the number of rides that have at least a pickup arrive time, and how many were on time, helping you evaluate overall service reliability during the selected period.',
  TOP_5_RENDERING_SERVICES:
    'Highlights the top 5 most frequently used transportation services based on completed rides, helping you understand utilization trends.',
  LIFE_SUSTAINING_RIDES_ON_TIME_PERFORMANCE:
    'Shows life-sustaining rides that have at least a pickup arrive time, along with their on-time performance, helping ensure critical trips are delivered reliably.',
  RIDES_AND_MEMBERS:
    'Shows the number of completed rides and unique members served each month, helping track utilization and member engagement over time.',
  ON_TIME_PERFORMANCE:
    'Displays the monthly on-time performance rate for rides with at least a pickup arrive time, calculated by comparing on-time rides to late rides, giving a clear view of service reliability trends.',
  LIFE_SUSTAINING_VS_ROUTINE:
    'Compares monthly counts of life-sustaining completed rides versus routine completed rides, helping you understand the mix of critical versus non-critical transportation.',
  RIDES_BY_STATUS:
    'Breaks down rides by status each month (e.g., completed, cancelled, incomplete), helping track performance trends over time.',
  RIDES_BY_COUNTY:
    'Shows the number of completed rides by pickup county each month, helping identify geographic demand patterns.',
  AVERAGE_MILES_TRAVELED_PER_RIDE:
    'Displays the average distance per completed ride each month, helping monitor transportation efficiency and cost trends.',
  RIDES_BY_PICKUP_TIME_HEAT_MAP:
    'Visualizes when completed rides occur throughout the day for each month, helping identify peak pickup times and optimize scheduling.',
};

export const RIDE_REPORT_TYPE_DESCRIPTIONS: Partial<
  Record<ReportType, string>
> = {
  [RIDE_REPORT_TYPES.RIDES_REPORT]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.RIDE_STATUS_OVERVIEW,
  [RIDE_REPORT_TYPES.INCOMPLETE_REPORT]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.INCOMPLETE_RIDE_BREAKDOWN,
  [RIDE_REPORT_TYPES.LS_INCOMPLETE_REPORT]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.LIFE_SUSTAINING_INCOMPLETE_RIDE_BREAKDOWN,
  [RIDE_REPORT_TYPES.COST_BY_COUNTY]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.COST_BY_COUNTY,
  [RIDE_REPORT_TYPES.RIDE_OTP_PERFORMANCE]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.RIDES_ON_TIME_PERFORMANCE,
  [RIDE_REPORT_TYPES.RENDERING_SERVICE]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.TOP_5_RENDERING_SERVICES,
  [RIDE_REPORT_TYPES.RIDE_LS_INTERVAL]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.LIFE_SUSTAINING_RIDES_ON_TIME_PERFORMANCE,
  [RIDE_REPORT_TYPES.RIDE_MEMBER_DATA]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.RIDES_AND_MEMBERS,
  [RIDE_REPORT_TYPES.ON_TIME_PERFORMANCE]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.ON_TIME_PERFORMANCE,
  [RIDE_REPORT_TYPES.RIDE_LS_ROUTINE]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.LIFE_SUSTAINING_VS_ROUTINE,
  [RIDE_REPORT_TYPES.RIDE_STATUS_OVERVIEW]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.RIDES_BY_STATUS,
  [RIDE_REPORT_TYPES.RIDE_COUNTY]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.RIDES_BY_COUNTY,
  [RIDE_REPORT_TYPES.RIDE_AVERAGE_MILES]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.AVERAGE_MILES_TRAVELED_PER_RIDE,
  [RIDE_REPORT_TYPES.RIDE_HEAT_MAP]:
    RIDE_REPORT_TABLE_DESCRIPTIONS.RIDES_BY_PICKUP_TIME_HEAT_MAP,
};

export type TableMonthlyType = (typeof tableType.monthly)[number];
export type TableCustomType = (typeof tableType.custom)[number];
